import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthService } from '../../services/api.service';
import { BrnService } from '../../services/brn.service';
import { Bot } from '../../interfaces/bot.interface';
import { HeaderComponent } from '../header/header';
import { StoreRefreshService } from '../../services/store-refresh.service';

@Component({
  selector: 'app-stores',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './stores.component.html',
  styleUrls: ['./stores.component.scss']
})
export class StoresComponent implements OnInit, OnDestroy {
  bots: Bot[] = [];
  loading = false;
  error = '';
  hasMore = true;
  loadingMore = false;
  currentOffset = 0;
  limit = 80;
  showAddBrandModal = false;
  newBrandUrl = '';
  isCreating = false;
  
  private refreshSubscription: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private storeRefreshService: StoreRefreshService,
    private router: Router,
    private brnService: BrnService
  ) {}

  ngOnInit() {
    this.loadBots();
    
    // Subscribe to refresh events
    this.refreshSubscription = this.storeRefreshService.refreshStores$.subscribe(() => {
      this.loadBots();
    });
  }

  ngOnDestroy() {
    this.refreshSubscription.unsubscribe();
  }

  @HostListener('window:scroll')
  onScroll() {
    // Check if user has scrolled to near the bottom of the page
    const threshold = 100; // Load more when 100px from bottom
    const position = window.pageYOffset + window.innerHeight;
    const max = document.documentElement.scrollHeight;

    if (position > max - threshold && this.hasMore && !this.loadingMore && !this.loading) {
      this.loadMoreBots();
    }
  }

  async loadBots() {
    this.loading = true;
    this.error = '';
    this.currentOffset = 0;

    try {
      const response = await firstValueFrom(this.authService.getBots(this.limit, this.currentOffset));
      
      if (response && response.bots) {
        this.bots = response.bots;
        this.hasMore = response.bots.length === this.limit && this.bots.length < response.total;
        this.currentOffset = this.bots.length;
      }
    } catch (error: any) {
      this.error = error.error?.message || 'Failed to load stores. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  async loadMoreBots() {
    if (this.loadingMore || !this.hasMore) {
      return;
    }

    this.loadingMore = true;
    this.error = '';

    try {
      const response = await firstValueFrom(this.authService.getBots(this.limit, this.currentOffset));
      
      if (response && response.bots) {
        this.bots = [...this.bots, ...response.bots];
        this.hasMore = response.bots.length === this.limit;
        this.currentOffset = this.bots.length;
      }
    } catch (error: any) {
      this.error = error.error?.message || 'Failed to load more stores.';
    } finally {
      this.loadingMore = false;
    }
  }

  getBotStatus(bot: Bot): string {
    // For demo purposes, randomly assign status based on bot_id
    const hash = bot.bot_id.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    
    return Math.abs(hash) % 3 === 0 ? 'NEW REPORT' : 'NO UPDATES';
  }

  trackByBotId(index: number, bot: Bot): string {
    return bot.bot_id;
  }

  navigateToStore(botId: string) {
    this.router.navigate(['/store', botId]);
  }

  addBrand() {
    this.showAddBrandModal = true;
  }

  onImageError(event: any, bot: Bot) {
    const initials = this.getBotInitials(bot.name);
    const svg = this.createInitialsSvg(initials);
    event.target.src = `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  private getBotInitials(name: string): string {
    if (!name || name.trim().length === 0) {
      return '??';
    }

    const words = name.trim().split(/\s+/);
    
    if (words.length === 1) {
      // Single word: take first 2 letters
      const word = words[0];
      return word.length >= 2 ? word.substring(0, 2).toUpperCase() : (word + '?').substring(0, 2).toUpperCase();
    } else {
      // Multiple words: take first letter from first word and first letter from second word
      const firstLetter = words[0].charAt(0);
      const secondLetter = words[1].charAt(0);
      return (firstLetter + secondLetter).toUpperCase();
    }
  }

  private createInitialsSvg(initials: string): string {
    return `<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="32" fill="#F3F4F6"/>
      <text x="32" y="38" font-family="sans-serif" font-size="20" font-weight="600" fill="#696969" text-anchor="middle">${initials}</text>
    </svg>`;
  }

  // Modal helpers
  closeAddBrandModal() {
    this.showAddBrandModal = false;
    this.newBrandUrl = '';
  }

  sanitizeBrandName(input: string): string {
    if (!input) return '';
    let value = input.trim().toLowerCase();
    // Remove protocol
    value = value.replace(/^https?:\/\//, '');
    // Remove www or m subdomain prefixes repeatedly
    value = value.replace(/^(www\.|m\.)+/,'');
    // Keep only host portion
    value = value.split('/')[0].split('?')[0].split('#')[0];
    // Remove port
    value = value.split(':')[0];
    // Extract second-level domain as brand candidate
    const parts = value.split('.').filter(Boolean);
    let candidate = parts.length >= 2 ? parts[parts.length - 2] : (parts[0] || '');
    candidate = candidate.replace(/[^a-z0-9\-\s]/g, ' ').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
    // Title-case
    candidate = candidate.replace(/\b\w/g, ch => ch.toUpperCase());
    return candidate;
  }

  async confirmAddBrand() {
    const name = this.sanitizeBrandName(this.newBrandUrl);
    if (!name) return;
    if (this.isCreating) return;
    this.isCreating = true;
    try {
      await firstValueFrom(this.brnService.createBot(name));
      this.closeAddBrandModal();
      await this.loadBots();
    } catch (e) {
      console.error('Failed to create brand', e);
    } finally {
      this.isCreating = false;
    }
  }
} 